import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';

import { version as RUNNING_WEB_VERSION } from '../../package.json';
import { fetchWebVersion, isWebUpdateEnabled } from './webVersion';

/**
 * Actualización de la app nativa.
 *
 * ─── Por qué hay DOS versiones y no una ────────────────────────────────────
 *
 * Un cambio de la capa web (React, textos, pantallas, arreglos) puede
 * entregarse sin tocar el APK: el updater descarga el bundle nuevo y la app lo
 * usa al siguiente arranque. Eso cubre el ~99 % de lo que se publica.
 *
 * Un cambio de la capa NATIVA (el plugin de GPS, un permiso nuevo, un plugin de
 * Capacitor nuevo) no puede entregarse así: el código nativo vive en el APK.
 *
 * Por eso el manifiesto declara `webVersion` y `nativeVersion` por separado. Sin
 * esa separación existe un fallo que no tiene arreglo desde la propia app:
 * entregar por OTA un bundle que llama a un plugin que el APK instalado no
 * tiene. La app arrancaría rota y el propio mecanismo de actualización estaría
 * dentro del bundle roto.
 *
 * ─── Por qué modo manual y no automático ───────────────────────────────────
 *
 * El modo automático del updater aplica la actualización cuando la app pasa a
 * segundo plano. En una app normal es lo correcto; aquí es peligroso: el
 * operario manda la app a segundo plano CONSTANTEMENTE durante un recorrido —
 * apaga la pantalla, atiende una llamada, abre el mapa. Aplicar entonces
 * reiniciaría el WebView a media captura.
 *
 * En modo manual decidimos nosotros, y la regla es simple y dura: **nunca con
 * un tramo de recorrido en curso**.
 *
 * ─── 🔴 Por qué `next()` NO se llama al descargar ──────────────────────────
 *
 * Porque no era manual del todo. La documentación del plugin dice que el bundle
 * encolado con `next()` se activa *«when the app backgrounds or restarts»*, y
 * durante un recorrido el operario manda la app a segundo plano continuamente:
 * apaga la pantalla, atiende una llamada, abre el mapa. Encolar al descargar
 * significaba que el WebView podía reiniciarse **a mitad de captura** — el fallo
 * exacto que el modo manual pretendía evitar.
 *
 * Ahora se descarga y se guarda el `id`, nada más. `next()` se llama pegado a
 * `reload()` en {@link applyPendingUpdate}, cuando ya se ha comprobado que no
 * hay tramo. Si no está encolado, no puede activarse solo: la garantía deja de
 * depender de la semántica del plugin en segundo plano.
 */

/** Contrato del `latest.json` que publica la CI. */
export interface UpdateManifest {
  /** Versión de la capa web. Si difiere, se entrega por OTA. */
  webVersion: string;
  bundleUrl: string;
  /** Checksum del zip. El updater lo verifica antes de instalar. */
  bundleChecksum?: string;
  /**
   * Versión de la capa nativa. Si difiere, **el OTA no basta**: hace falta un
   * APK nuevo. Sube solo cuando cambia código nativo, no con cada release.
   */
  nativeVersion: string;
  apkUrl?: string;
  apkChecksum?: string;
  /** Bloquea el uso de la app hasta actualizar. Para correcciones críticas. */
  mandatory?: boolean;
}

export type UpdateStatus =
  | { kind: 'up-to-date' }
  /**
   * Hay bundle nuevo y ya está descargado, esperando a que alguien decida
   * aplicarlo. `bundleId` es lo que hay que pasarle a
   * {@link applyPendingUpdate}: sin él no hay forma de encolarlo.
   */
  | { kind: 'web-ready'; version: string; bundleId: string; mandatory: boolean }
  /** Hace falta APK nuevo: el OTA no puede resolverlo. */
  | {
      kind: 'native-required';
      /** La HUELLA nativa. Identidad interna del update — no se enseña. */
      version: string;
      /**
       * La versión de la app que trae ese APK. Es lo que se ENSEÑA: un operario
       * no puede leer `1cb80a6568db`, y además este número es el mismo que verá
       * en Ajustes → Aplicaciones cuando termine de instalar.
       */
      webVersion: string;
      apkUrl?: string;
      /** El plugin se niega a instalar si no cuadra con el fichero bajado. */
      apkChecksum?: string;
      mandatory: boolean;
    }
  /**
   * WEB: este dominio sirve otra versión que la que corre. No hay nada que
   * descargar: aplicar es recargar (`versionManager.applyWebUpdate`).
   */
  | { kind: 'web-available'; version: string; mandatory: boolean }
  | { kind: 'unavailable' };

/**
 * Versión nativa que lleva ESTE APK. Se compara con la del manifiesto para
 * saber si un OTA basta.
 *
 * Vive en su propia variable de entorno y NO en `package.json` a propósito: la
 * versión del paquete sube en cada release de la web, y usarla aquí obligaría a
 * reinstalar el APK en cada publicación — justo lo que el OTA viene a evitar.
 * Solo se sube a mano cuando cambia código nativo.
 */
const BUILT_IN_NATIVE_VERSION = import.meta.env.VITE_NATIVE_VERSION ?? '1.0.0';

const MANIFEST_URL = import.meta.env.VITE_UPDATE_MANIFEST_URL ?? '';

function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/**
 * Confirma al updater que el bundle ha arrancado bien.
 *
 * 🔴 **Si esto no se llama, el updater REVIERTE al bundle anterior** pasado el
 * timeout, dando por hecho que la versión nueva está rota. Es la red de
 * seguridad que hace que un OTA malo no deje la tablet inservible… y también la
 * forma más fácil de romperlo todo si se olvida.
 *
 * Se llama lo antes posible y NO se espera a ninguna petición de red: solo
 * confirma que el JavaScript cargó, no que la aplicación funcione.
 */
export function notifyAppReady(): void {
  if (!isNative()) return;
  void CapacitorUpdater.notifyAppReady().catch(() => {
    // Si el plugin no está (build viejo), no hay nada que confirmar.
  });
}

async function fetchManifest(): Promise<UpdateManifest | null> {
  if (!MANIFEST_URL) return null;
  try {
    const res = await fetch(`${MANIFEST_URL}?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as UpdateManifest;
  } catch {
    // Sin cobertura no hay actualización que comprobar, y no es un error:
    // es el estado normal de una tablet en carretera.
    return null;
  }
}

/**
 * Comprueba si hay actualización y, si es de la capa web, **la deja descargada
 * y lista** para el siguiente arranque.
 *
 * No aplica nada por su cuenta: quien decide cuándo reiniciar es
 * {@link applyPendingUpdate}, y solo cuando no hay un recorrido en curso.
 */
export async function checkForUpdate(): Promise<UpdateStatus> {
  if (!isNative()) return checkWebUpdate();

  const manifest = await fetchManifest();
  if (!manifest) return { kind: 'unavailable' };

  const mandatory = manifest.mandatory === true;

  // La capa nativa manda: si el APK está por detrás, un bundle web nuevo podría
  // llamar a un plugin que este APK no tiene. Se avisa y NO se descarga nada.
  if (manifest.nativeVersion !== BUILT_IN_NATIVE_VERSION) {
    return {
      kind: 'native-required',
      version: manifest.nativeVersion,
      webVersion: manifest.webVersion,
      apkUrl: manifest.apkUrl,
      apkChecksum: manifest.apkChecksum,
      mandatory,
    };
  }

  // 🔴 La versión que corre la declara el BUNDLE que corre, no el plugin.
  //
  // Antes se comparaba contra `CapacitorUpdater.current()`, y para el bundle que
  // viaja dentro del APK ese valor es el del `builtin` — que no coincide nunca
  // con el del manifiesto. Resultado: un APK recién instalado se descargaba un
  // zip idéntico al que ya llevaba dentro.
  //
  // `package.json` es exacto en los dos casos, porque se compila DENTRO del
  // propio bundle: si corre el builtin dice la versión del builtin, y si corre
  // uno instalado por OTA dice la suya. También cubre el caso de que el updater
  // haya revertido a la anterior por un `notifyAppReady` que no llegó.
  if (manifest.webVersion === RUNNING_WEB_VERSION) {
    return { kind: 'up-to-date' };
  }

  try {
    // ¿Ya está descargado de una comprobación anterior? Pasa siempre que el
    // operario cierre el aviso: sin esto se re-descargaría el mismo zip cada
    // cuarto de hora, con datos móviles de por medio.
    const existing = await findDownloadedBundle(manifest.webVersion);
    if (existing) {
      return {
        kind: 'web-ready',
        version: manifest.webVersion,
        bundleId: existing,
        mandatory,
      };
    }

    const bundle = await CapacitorUpdater.download({
      url: manifest.bundleUrl,
      version: manifest.webVersion,
      checksum: manifest.bundleChecksum,
    });

    // Ni `next` ni `set` aquí: ver la nota del encabezado. Solo se descarga; el
    // momento de aplicar lo decide `applyPendingUpdate`.
    return {
      kind: 'web-ready',
      version: manifest.webVersion,
      bundleId: bundle.id,
      mandatory,
    };
  } catch {
    // Descarga fallida o checksum que no cuadra: se sigue con el bundle actual,
    // que es la respuesta correcta. Se reintentará en la próxima comprobación.
    return { kind: 'unavailable' };
  }
}

/**
 * En web la autoridad es `/version.json` del propio dominio (ver
 * `webVersion.ts`). «Distinta», no «mayor»: un rollback en Vercel también
 * tiene que llegar a las pestañas abiertas.
 */
async function checkWebUpdate(): Promise<UpdateStatus> {
  if (!isWebUpdateEnabled()) return { kind: 'unavailable' };
  const served = await fetchWebVersion();
  if (!served) return { kind: 'unavailable' };
  if (served.version === RUNNING_WEB_VERSION) return { kind: 'up-to-date' };
  return { kind: 'web-available', version: served.version, mandatory: served.mandatory };
}

/**
 * Busca entre los bundles ya descargados uno de esta versión que esté sano.
 *
 * Se descartan los marcados `error` o en proceso de borrado: reofrecer un
 * bundle que el propio updater dio por malo sería meter a la tablet en un bucle
 * de arranque y rollback.
 */
async function findDownloadedBundle(version: string): Promise<string | null> {
  try {
    const { bundles } = await CapacitorUpdater.list();
    const usable = bundles.find(
      (b) =>
        b.version === version &&
        b.id !== 'builtin' &&
        (b.status === 'success' || b.status === 'pending')
    );
    return usable?.id ?? null;
  } catch {
    // Un APK viejo sin `list()` o un fallo del puente: se descarga y ya.
    return null;
  }
}

/**
 * Aplica una actualización ya descargada reiniciando el WebView.
 *
 * 🔴 **`hasActiveSegment` es la única salvaguarda que impide reiniciar a mitad
 * de un recorrido.** El servicio nativo de GPS sobreviviría al reinicio —graba
 * en SQLite y no depende del WebView—, pero el drenaje del buffer, el mapa en
 * vivo y el cronómetro se cortarían delante del operario. Reiniciar la app en
 * ese momento es exactamente el fallo que la app nativa viene a evitar.
 */
export async function applyPendingUpdate(
  bundleId: string,
  hasActiveSegment: boolean
): Promise<boolean> {
  if (!isNative() || hasActiveSegment || !bundleId) return false;
  try {
    // Los dos pegados y en este orden: `next` marca cuál se usa al recargar y
    // `reload` recarga. Entre una línea y otra no hay ventana en la que el
    // sistema pueda aplicar el cambio por su cuenta.
    await CapacitorUpdater.next({ id: bundleId });
    await CapacitorUpdater.reload();
    return true;
  } catch {
    return false;
  }
}
